import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import {
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ScrollView,
  Pressable,
  StyleSheet
} from 'react-native'
import { Formik } from 'formik'
import * as yup from 'yup'
import MaskInput, { Masks, formatWithMask, Mask } from 'react-native-mask-input'
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons'

// styles
import { colorPalette, globalStyles } from '../../../static/styles/global'

// i18n
import { en as formSelectEn } from '../../../i18n/FormSelect'
import { en as personalInfoEn } from '../../../i18n/PersonalInformationForms'

// components
import CustomButton from '../../shared/button/Button'
import Select from '../../shared/form/Select'
import Card from '../../shared/card/Card'
import CardSeparator from '../../shared/card/CardSeparator'
import ScreenStatus from '../ScreenStatus'

import {
  customMasks,
  deserializeDOB,
  getUnmaskedValue,
  mapSelectKey,
  serializeDOB,
  setMaskedValue,
  validateDate,
  validateDOBFormat,
  validateDOBYear,
  validateSSN,
  validateZipCode,
  validateserialnumber,
  validate6to9,
  validateserial3to5,
  validatecombination,
  validatelength
} from '../../../utils'
import SelectKeyValue from '../../shared/form/SelectKeyValue'
import { Loading } from '../../shared/navigator/Loading'

// stores
import { usePersonalStore } from '../../../stores/personalInfo'

// types
import {
  PersonalStatuses,
  PersonalStatusesLocked,
  PersonalData
} from '../../../types/personalTypes'
import HomeDetailsHeader from '../HomeDetailsHeader'
import PersonalBasicInfoSkeleton from '../../../skeletons/PersonalBasicInfoSkeleton'
import { SafeAreaView } from 'react-native-safe-area-context'
//translation
import { useTranslation } from 'react-i18next'
import SearchKeyValue from '../../shared/form/SearchKeyValue'
import CustomDatePickerModal from '../../shared/calendar/CustomDatePickerModal'

import moment from 'moment'
import { useFocusEffect } from '@react-navigation/native'
// initial data
type InitialValues = Pick<
  PersonalData,
  | 'firstName'
  | 'middleInitial'
  | 'lastName'
  | 'ssn'
  | 'addressLine1'
  | 'addressLine2'
  | 'city'
  | 'stateValue'
  | 'zip'
  | 'genderValue'
  | 'maritalStatusValue'
> &
  Record<'dateOfBirth', string>

const initialValues: InitialValues = {
  firstName: '',
  middleInitial: '',
  lastName: '',
  ssn: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateValue: '',
  zip: '',
  dateOfBirth: '',
  genderValue: '',
  maritalStatusValue: ''
}

const BasicInformationForm = ({ navigation, route }: any): React.ReactNode => {
  const { t } = useTranslation()
  const [ssnVisible, setSsnVisible] = useState(false)
  const [status, setStatus] = useState<PersonalStatuses | null>(null)
  const [visibledate, setVisibleDate] = useState(false)
  const formikRef = useRef(null)

  const [show, setShow] = useState(false)

  // params
  const { params } = route
  const {
    mostRecentUserOnBoardings: userId,
    states,
    genders,
    maritalStatuses
  } = params
  const [searchText, setSearchText] = useState('')
  const [filteredStates, setFilteredStates] = useState([])

  // stores' data
  const { loading, error, personalData, fetchData, updateData } =
    usePersonalStore()

  useEffect(() => {
    fetchData(userId)
  }, [])

  useEffect(() => {
    if (formikRef?.current) {
      setMaskedValue('ssn', customMasks.USA_SSN, formikRef)
      setMaskedValue('zip', customMasks.USA_ZIPCODE, formikRef)
      setMaskedValue(
        'dateOfBirth',
        Masks.DATE_MMDDYYYY,
        formikRef,
        deserializeDOB
      )
    }

    if (personalData?.statusDescription)
      setStatus(personalData.statusDescription)

    return () => { }
  }, [formikRef?.current, loading])


  const handletext = (text, setFieldValue) => {
    if (text.length > 0) {
      const index = states.findIndex((option) => option.stateDescription.toLowerCase() === (text.toLowerCase()))
      if (index != -1) {
        setFieldValue("stateValue", states[index].stateValue)
      }
    }
    else {
      setFieldValue("stateValue", "")
    }
    setDummy(text)
  }


  const [dummy, setDummy] = useState(personalData?.stateValue || '')
  useEffect(() => {
    if (dummy.length > 0) {
      const filtered = states.filter((option) =>
        option.stateDescription.toLowerCase().includes(dummy.toLowerCase())
      )
      setFilteredStates(filtered)
    } else {
      setFilteredStates(states)
    }
  }, [dummy])

  useFocusEffect(
    React.useCallback(() => {
      const findDescription = () => {
        const value = states.findIndex(
          (option) => option.stateValue == personalData?.stateValue
        )
        if (value > -1) {
          setDummy(states[value].stateDescription)
        }
      }
      findDescription()
    }, [])
  )
  if (loading) return <PersonalBasicInfoSkeleton />

  if (error) return <ScreenStatus message={error} />

  const validatefirst = t('validatefirstName')

  const validatelastName = t('validatelastName')

  const validatessn = t('validatessn')

  const validatessnFormat = t('validatessnFormat')

  const validateaddress = t('validateaddress')

  const validatecity = t('validatecity')

  const validatestate = t('validatestate')

  const validatezipCode = t('validatezipCode')

  const validatezipCodeFormat = t('validatezipCodeFormat')

  const validatedob = t('validatedob')

  const validatedobFormat = t('validatedobFormat')

  const validatedobValideYear = t('validatedobValideYear')

  const validatedobValide = t('validatedobValide')

  const validategender = t('validategender')

  const validatemaritalStatus = t('validatemaritalStatus')

  const validatessnpositions069 = t('validatessnpositions069')

  const validatessnposition45 = t('validatessnposition45')

  const validatessnposition0000 = t('validatessnposition0000')

  const validatessnserial = t('validatessnserial')
  // schema
  const basicInformationSchema = yup.object({
    firstName: yup.string().trim().nullable().required(validatefirst),

    // .min(1, 'Should be at least 1 characters')

    // .max(12, 'Should be no more than 12 characters'),

    middleInitial: yup.string().trim().length(1, 'Should be 1 character'),

    lastName: yup.string().trim().nullable().required(validatelastName),

    // .min(1, 'Should be at least 1 characters')

    // .max(16, 'Should be no more than 16 characters'),

    ssnLastFour: yup.string().nullable(),

    ssn: yup

      .string()

      .nullable()

      .when('ssnLastFour', (ssnLastFourVal) => {
        if (
          ssnLastFourVal[0] === null ||
          ssnLastFourVal[0] === '' ||
          ssnLastFourVal[0] === undefined
        ) {
          return yup

            .string()

            .nullable()

            .required(validatessn)

            .test('is-ssn', '', (val, { createError }) => {
              return !validateSSN(getUnmaskedValue(val, customMasks.USA_SSN))
                .valid
                ? createError({
                  message: validateSSN(
                    getUnmaskedValue(val, customMasks.USA_SSN)
                  ).message,

                  path: ''
                })
                : true
            })

            .test('is-validlength', validatessnFormat, (val) => {
              return validatelength(getUnmaskedValue(val, customMasks.USA_SSN))
            })

            .test('is-1to3andcombinations', validatessnpositions069, (val) => {
              return validatecombination(val)
            })

            .test('is-3to5valid', validatessnposition45, (val) => {
              return validateserial3to5(val)
            })

            .test('is-5to9valid', validatessnposition0000, (val) => {
              return validate6to9(val)
            })

            .test('is-sequential', validatessnserial, (val) => {
              return validateserialnumber(val)
            })
        } else {
          return yup

            .string()

            .nullable()

            .test('is-ssn', '', (val, { createError }) => {
              return val &&
                !validateSSN(getUnmaskedValue(val, customMasks.USA_SSN)).valid
                ? createError({
                  message: validateSSN(
                    getUnmaskedValue(val, customMasks.USA_SSN)
                  ).message,

                  path: ''
                })
                : true
            })

            .test('is-validlength', validatessnFormat, (val) => {
              return validatelength(getUnmaskedValue(val, customMasks.USA_SSN))
            })

            .test('is-1to3andcombinations', validatessnpositions069, (val) => {
              return validatecombination(val)
            })

            .test('is-3to5valid', validatessnposition45, (val) => {
              return validateserial3to5(val)
            })

            .test('is-5to9valid', validatessnposition0000, (val) => {
              return validate6to9(val)
            })

            .test('is-sequential', validatessnserial, (val) => {
              return validateserialnumber(val)
            })
        }
      }),

    addressLine1: yup

      .string()

      .trim()

      .nullable()

      .required(validateaddress)

      .min(1, 'Should be at least 1 characters')

      .max(24, 'Should be no more than 24 characters'),

    addressLine2: yup

      .string()

      .trim()

      .nullable()

      .max(24, 'Should be no more than 24 characters'),

    city: yup

      .string()

      .trim()

      .nullable()

      .required(validatecity)

      .min(1, 'Should be at least 1 characters')

      .max(22, 'Should be no more than 22 characters'),

    stateValue: yup.string().required(validatestate),

    zip: yup

      .string()

      .nullable()

      .required(validatezipCode)

      .test('is-zipcode', validatezipCodeFormat, (val) => {
        return validateZipCode(val)
      }),

    dateOfBirth: yup

      .string()

      .nullable()

      .required(validatedob)

      .test('is-date-format', validatedobFormat, (val) => {
        return validateDOBFormat(val)
      })

      .test('is-date-valid', validatedobValide, (val) => {
        return validateDate(val)
      })

      .test('is-date-valid-year', validatedobValideYear, (val) => {
        return validateDOBYear(val, 10, 120)
      }),

    genderValue: yup.string().nullable().required(validategender),

    maritalStatusValue: yup.string().nullable().required(validatemaritalStatus)
  })



  return (
    personalData &&
    status && (
      <>
        <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
          <View style={[globalStyles.horizontalPadGap]}>
            <HomeDetailsHeader
              title={t('personalinformation')}
              description={t('personaldescription')}
            />
          </View>
        </View>
        <View style={[globalStyles.containerOverlap]}>
          <Formik
            enableReinitialize
            innerRef={formikRef}
            initialValues={{
              ...initialValues,
              ...personalData
            }}
            validationSchema={basicInformationSchema}
            onSubmit={(values, actions) => {
              actions.resetForm()

              // TODO: call action to save values to DB - remove white spaces from values entries on frontend
              const basicInfoDto = {
                ...values,
                ssn: getUnmaskedValue(values.ssn, customMasks.USA_SSN),
                zip: getUnmaskedValue(values.zip, customMasks.USA_ZIPCODE),
                dateOfBirth: serializeDOB(values.dateOfBirth)
              }
              // console.log('basicInfoDto:', basicInfoDto)

              // status will be INCOMPLETE
              const submitted = { Submitted: false }

              let {
                essUserOnBoardingKey,
                employeeEmailAddress,
                ssnLastFour,
                statusDescription,
                ...personalDto
              } = basicInfoDto

              personalDto = { ...personalDto, ...submitted }

              // console.log('personalDto:', personalDto)

              // update the database when the status neither PENDING nor COMPLETED
              if (!PersonalStatusesLocked.includes(status)) {
                updateData(userId, personalDto)
              }

              // go next to contact information form
              navigation.replace('PersonalContactInfromation')
            }}
          >
            {(props) => (
              <View>
                <Card>
                  <View
                    style={[
                      globalStyles.hDirection,
                      globalStyles.vCenterItems,
                      globalStyles.hBetweenItems
                    ]}
                  >
                    <Text
                      style={[
                        globalStyles.text,
                        globalStyles.fwBold,
                        globalStyles.fs18,
                        globalStyles.blackTextColor
                      ]}
                    >
                      {t('basicinformation')}
                    </Text>
                    <Text
                      style={[
                        globalStyles.text,
                        globalStyles.fs14,
                        globalStyles.grayTextColor
                      ]}
                    >
                      <Text style={styles.required}>*</Text>
                    </Text>
                  </View>
                  <CardSeparator light={true} />
                  <View>
                    {/* <Text>{JSON.stringify(props, null, 2)}</Text> */}
                    <View style={[globalStyles.hDirection, globalStyles.mb2]}>
                      <View style={{ width: '64%', marginRight: '4%' }}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('firstname')}<Text style={styles.required}>*</Text>
                        </Text>
                        <TextInput
                          style={[
                            globalStyles.input,
                            globalStyles.inputLight,
                            PersonalStatusesLocked.includes(status) &&
                            globalStyles.inputReadOnly,
                            props.touched.firstName &&
                            props.errors.firstName &&
                            globalStyles.inputError,
                            { textTransform: 'uppercase' }
                          ]}
                          placeholder={''}
                          onChangeText={props.handleChange('firstName')}
                          value={props.values.firstName || ''}
                          onBlur={props.handleBlur('firstName')}
                          editable={!PersonalStatusesLocked.includes(status)}
                        />
                        {props.touched.firstName && props.errors.firstName && (
                          <Text style={globalStyles.dangerAlert}>
                            {props.errors.firstName}
                          </Text>
                        )}
                      </View>
                      <View style={{ width: '32%' }}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('middlename')}
                        </Text>
                        <TextInput
                          style={[
                            globalStyles.input,
                            globalStyles.inputLight,
                            PersonalStatusesLocked.includes(status) &&
                            globalStyles.inputReadOnly,
                            { textTransform: 'uppercase' }
                          ]}
                          placeholder={''}
                          onChangeText={props.handleChange('middleInitial')}
                          value={props.values.middleInitial || ''}
                          onBlur={props.handleBlur('middleInitial')}
                          maxLength={1}
                          editable={!PersonalStatusesLocked.includes(status)}
                        />
                      </View>
                    </View>
                    <View style={[globalStyles.mb2]}>
                      <Text
                        style={[
                          globalStyles.inputLabel,
                          globalStyles.inputLabelLarge,
                          globalStyles.inputLabelPrimary
                        ]}
                      >
                        {t('lastname')}<Text style={styles.required}>*</Text>
                      </Text>
                      <TextInput
                        style={[
                          globalStyles.input,
                          globalStyles.inputLight,
                          PersonalStatusesLocked.includes(status) &&
                          globalStyles.inputReadOnly,
                          props.touched.lastName &&
                          props.errors.lastName &&
                          globalStyles.inputError,
                          { textTransform: 'uppercase' }
                        ]}
                        onChangeText={props.handleChange('lastName')}
                        value={props.values.lastName || ''}
                        onBlur={props.handleBlur('lastName')}
                        editable={!PersonalStatusesLocked.includes(status)}
                      />
                      {props.touched.lastName && props.errors.lastName && (
                        <Text style={globalStyles.dangerAlert}>
                          {props.errors.lastName}
                        </Text>
                      )}
                    </View>

                    {/* begin */}
                    <View style={[globalStyles.mb2]}>
                      <View
                        style={[
                          globalStyles.hDirection,
                          globalStyles.hBetweenItems,
                          globalStyles.vCenterItems
                        ]}
                      >
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('socialnumber')}<Text style={styles.required}>*</Text>
                        </Text>
                        {false && (
                          <TouchableOpacity onPress={() => { }}>
                            <MaterialCommunityIcons
                              name="information"
                              size={18}
                              color={colorPalette.secondary}
                            />
                          </TouchableOpacity>
                        )}
                      </View>
                      <View style={[globalStyles.inputWithIconWrapper]}>
                        <MaskInput
                          style={[
                            globalStyles.input,
                            globalStyles.inputLight,
                            PersonalStatusesLocked.includes(status) &&
                            globalStyles.inputReadOnly,
                            props.touched.ssn &&
                            props.errors.ssn &&
                            globalStyles.inputError
                          ]}
                          value={props.values.ssn || ''}
                          onChangeText={props.handleChange('ssn')}
                          onBlur={props.handleBlur('ssn')}
                          mask={customMasks.USA_SSN}
                          // secureTextEntry={!ssnVisible}
                          // inputMode="text"
                          // keyboardType="numeric"
                          placeholder={`XXX-XX-${personalData.ssnLastFour || 'XXXX'
                            }`}
                          placeholderTextColor={
                            personalData.ssnLastFour
                              ? colorPalette.dark
                              : colorPalette.lightDark
                          }
                          // autoComplete="off"
                          // autoCorrect={false}
                          editable={!PersonalStatusesLocked.includes(status)}
                        />
                        {/* {props.values.ssn !== '' && (
                          <TouchableOpacity
                            style={[
                              globalStyles.inputWithIcon,
                              globalStyles.inputWithIconEnd
                            ]}
                            onPress={() => setSsnVisible(!ssnVisible)}
                          >
                            <Ionicons
                              name={`${
                                ssnVisible ? 'eye-off-outline' : 'eye-outline'
                              }`}
                              size={24}
                              color={colorPalette.secondary}
                            />
                          </TouchableOpacity>
                        )} */}
                      </View>
                      {props.touched.ssn && props.errors.ssn && (
                        <Text style={globalStyles.dangerAlert}>
                          {props.errors.ssn}
                        </Text>
                      )}
                    </View>
                    {/* end */}

                    <View style={[globalStyles.mb2]}>
                      <Text
                        style={[
                          globalStyles.inputLabel,
                          globalStyles.inputLabelLarge,
                          globalStyles.inputLabelPrimary
                        ]}
                      >
                        {t('address')}<Text style={styles.required}>*</Text>
                      </Text>
                      <TextInput
                        style={[
                          globalStyles.input,
                          globalStyles.inputLight,
                          PersonalStatusesLocked.includes(status) &&
                          globalStyles.inputReadOnly,
                          props.touched.addressLine1 &&
                          props.errors.addressLine1 &&
                          globalStyles.inputError, { textTransform: 'uppercase' }
                        ]}
                        placeholder={''}
                        onChangeText={props.handleChange('addressLine1')}
                        value={props.values.addressLine1 || ''}
                        onBlur={props.handleBlur('addressLine1')}
                        editable={!PersonalStatusesLocked.includes(status)}
                      />
                      {props.touched.addressLine1 &&
                        props.errors.addressLine1 && (
                          <Text style={globalStyles.dangerAlert}>
                            {props.errors.addressLine1}
                          </Text>
                        )}
                    </View>
                    <View style={[globalStyles.mb2]}>
                      <Text
                        style={[
                          globalStyles.inputLabel,
                          globalStyles.inputLabelLarge,
                          globalStyles.inputLabelPrimary
                        ]}
                      >
                        {t('Address 2')}
                      </Text>
                      <TextInput
                        style={[
                          globalStyles.input,
                          globalStyles.inputLight,
                          PersonalStatusesLocked.includes(status) &&
                          globalStyles.inputReadOnly,
                          props.touched.addressLine2 &&
                          props.errors.addressLine2 &&
                          globalStyles.inputError, { textTransform: 'uppercase' }
                        ]}
                        placeholder={''}
                        onChangeText={props.handleChange('addressLine2')}
                        value={props.values.addressLine2 || ''}
                        onBlur={props.handleBlur('addressLine2')}
                        maxLength={24}
                        editable={!PersonalStatusesLocked.includes(status)}
                      />
                      {props.touched.addressLine2 &&
                        props.errors.addressLine2 && (
                          <Text style={globalStyles.dangerAlert}>
                            {props.errors.addressLine2}
                          </Text>
                        )}
                    </View>
                    <View style={[globalStyles.mb2]}>
                      <Text
                        style={[
                          globalStyles.inputLabel,
                          globalStyles.inputLabelLarge,
                          globalStyles.inputLabelPrimary
                        ]}
                      >
                        {t('city')}<Text style={styles.required}>*</Text>
                      </Text>
                      <TextInput
                        style={[
                          globalStyles.input,
                          globalStyles.inputLight,
                          PersonalStatusesLocked.includes(status) &&
                          globalStyles.inputReadOnly,
                          props.touched.city &&
                          props.errors.city &&
                          globalStyles.inputError, { textTransform: 'uppercase' }
                        ]}
                        placeholder={''}
                        onChangeText={props.handleChange('city')}
                        value={props.values.city || ''}
                        onBlur={props.handleBlur('city')}
                        editable={!PersonalStatusesLocked.includes(status)}
                      />
                      {props.touched.city && props.errors.city && (
                        <Text style={globalStyles.dangerAlert}>
                          {props.errors.city}
                        </Text>
                      )}
                    </View>
                    <View style={[globalStyles.hDirection, globalStyles.mb2]}>
                      <View style={[{ width: '48%', marginRight: '2%' }]}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('state')}<Text style={styles.required}>*</Text>
                        </Text>
                        {/* <SearchKeyValue
                          title={'States   '}
                          options={filteredStates}
                          selectedOption={mapSelectKey(
                            states,
                            personalData.stateValue
                          )}
                          onSelect={(option: string) => {
                            props.setFieldValue('stateValue', option)
                          }}
                          status={{
                            touched: props.touched.stateValue,
                            error: props.errors.stateValue
                          }}
                          isDisabled={PersonalStatusesLocked.includes(status)}
                          searchable={true}
                          searchText={searchText}
                          onSearchChange={setSearchText}
                        /> */}


                        <Pressable disabled={PersonalStatusesLocked.includes(status)} onPress={() => setShow(!show)}>
                          <TextInput
                            style={[
                              globalStyles.input,
                              globalStyles.inputLight,
                              PersonalStatusesLocked.includes(status) &&
                              globalStyles.inputReadOnly,
                              props.touched.stateValue &&
                              props.errors.stateValue &&
                              globalStyles.inputError,
                              { position: 'relative' },
                              { textTransform: 'uppercase' }
                            ]}
                            onChangeText={(text) => handletext(text, props.setFieldValue)}
                            value={dummy}
                            editable={!PersonalStatusesLocked.includes(status)}
                          />
                          <View
                            style={{
                              position: 'absolute',
                              right: 6,

                              bottom: 11
                            }}
                          >
                            <Ionicons
                              name="chevron-down"
                              size={23}
                              color={colorPalette.secondary}
                            />
                          </View>
                        </Pressable>

                        {show && (
                          <ScrollView
                            persistentScrollbar={true}
                            style={{
                              backgroundColor: "#f0f8ff",
                              borderRadius: 8,
                              paddingVertical: 18,
                              paddingHorizontal: 20,
                              maxHeight: 150,

                              // position: 'absolute',
                              // top: 50,
                              left: 0,
                              right: 0,
                              zIndex: 9999
                            }}
                          >
                            {filteredStates?.map((item, index) => (
                              <TouchableOpacity
                                style={{
                                  paddingVertical: 8,
                                  paddingHorizontal: 16,
                                  borderBottomWidth: 1,
                                  borderBottomColor: colorPalette.lightDark,
                                  flexDirection: 'row',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                }}
                                key={index}
                                onPress={() => {
                                  props.setFieldValue(
                                    'stateValue',
                                    item.stateValue
                                  )
                                  setDummy(item.stateDescription)
                                  setShow(false)
                                }}
                              >
                                <Text>{item.stateDescription}</Text>
                              </TouchableOpacity>
                            ))}
                          </ScrollView>
                        )}
                        {props.touched.stateValue &&
                          props.errors.stateValue && (
                            <Text style={globalStyles.dangerAlert}>
                              {props.errors.stateValue}
                            </Text>
                          )}
                      </View>
                      <View style={[{ width: '48%', marginLeft: '2%' }]}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('zipcode')}<Text style={styles.required}>*</Text>
                        </Text>
                        <MaskInput
                          style={[
                            globalStyles.input,
                            globalStyles.inputLight,
                            PersonalStatusesLocked.includes(status) &&
                            globalStyles.inputReadOnly,
                            props.touched.zip &&
                            props.errors.zip &&
                            globalStyles.inputError
                          ]}
                          value={props.values.zip || ''}
                          onChangeText={props.handleChange('zip')}
                          onBlur={props.handleBlur('zip')}
                          mask={customMasks.USA_ZIPCODE}
                          keyboardType="numeric"
                          editable={!PersonalStatusesLocked.includes(status)}
                        />
                        {props.touched.zip && props.errors.zip && (
                          <Text style={globalStyles.dangerAlert}>
                            {props.errors.zip}
                          </Text>
                        )}
                      </View>
                    </View>
                    <View >
                      <View style={[globalStyles.mb2]}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary,

                          ]}
                        >
                          {t('dateofbirth')}<Text style={styles.required}>*</Text>
                        </Text>
                        <View style={[globalStyles.inputWithIconWrapper]}>
                          <MaskInput
                            style={[
                              globalStyles.input,
                              globalStyles.inputLight,
                              PersonalStatusesLocked.includes(status) &&
                              globalStyles.inputReadOnly,
                              props.touched.dateOfBirth &&
                              props.errors.dateOfBirth &&
                              globalStyles.inputError,
                              { paddingRight: 40 }
                            ]}
                            value={props.values.dateOfBirth || ''}
                            // onChangeText={props.handleChange('dateOfBirth')}
                            onChangeText={(text) => {
                              props.handleChange('dateOfBirth')(text);
                            }}
                            onBlur={props.handleBlur('dateOfBirth')}
                            mask={Masks.DATE_MMDDYYYY}
                            keyboardType="numeric"
                            placeholder="mm/dd/yyyy"
                            editable={!PersonalStatusesLocked.includes(status)}
                          />
                          <TouchableOpacity
                            style={[
                              globalStyles.inputWithIcon,
                              globalStyles.inputWithIconEnd,
                              { bottom: 11 }
                            ]}
                            onPress={() => {
                              if (!PersonalStatusesLocked.includes(status)) {
                                setVisibleDate(true)
                              }
                            }}
                            disabled={PersonalStatusesLocked.includes(status)}
                          >
                            <Ionicons
                              name="calendar-outline"
                              size={22}
                              color={PersonalStatusesLocked.includes(status) ? colorPalette.lightDark : colorPalette.secondary}
                            />
                          </TouchableOpacity>
                        </View>
                        <CustomDatePickerModal
                          visible={visibledate && !PersonalStatusesLocked.includes(status)}
                          value={
                            props.values.dateOfBirth &&
                              moment(props.values.dateOfBirth, 'MM/DD/YYYY', true).isValid()
                              ? moment(props.values.dateOfBirth, 'MM/DD/YYYY').toDate()
                              : new Date('2000-01-01')
                          }
                          minimumDate={new Date(1920, 0, 1)}
                          maximumDate={new Date()}
                          onConfirm={(date) => {
                            const selectedDate = moment(date).format('MM/DD/YYYY')
                            setVisibleDate(false)
                            props.setFieldValue('dateOfBirth', selectedDate)
                          }}
                          onCancel={() => setVisibleDate(false)}
                        />
                        {props.touched.dateOfBirth &&
                          props.errors.dateOfBirth && (
                            <Text style={globalStyles.dangerAlert}>
                              {props.errors.dateOfBirth}
                            </Text>
                          )}
                      </View>
                    </View>

                    <View style={[globalStyles.hDirection, globalStyles.mb2]}>
                      <View
                        style={[
                          { width: '48%', marginRight: '2%' }
                        ]}
                      >
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('gender')}<Text style={styles.required}>*</Text>
                        </Text>
                        <SelectKeyValue
                          title={t('gender')}
                          options={genders}
                          selectedOption={mapSelectKey(
                            genders,
                            personalData.genderValue
                          )}
                          onSelect={(option: string) => {
                            props.setFieldValue('genderValue', option)
                          }}
                          status={{
                            touched: props.touched.genderValue,
                            error: props.errors.genderValue
                          }}
                          isDisabled={PersonalStatusesLocked.includes(status)}
                        />

                        {props.touched.genderValue &&
                          props.errors.genderValue && (
                            <Text style={globalStyles.dangerAlert}>
                              {props.errors.genderValue}
                            </Text>
                          )}
                      </View>
                      <View
                        style={[{ width: '48%', marginLeft: '2%' }]}
                      >
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('maritalstatus')}<Text style={styles.required}>*</Text>
                        </Text>
                        <SelectKeyValue
                          title={t('maritalstatus')}
                          options={maritalStatuses}
                          selectedOption={mapSelectKey(
                            maritalStatuses,
                            personalData.maritalStatusValue
                          )}
                          onSelect={(option: string) => {
                            props.setFieldValue('maritalStatusValue', option)
                          }}
                          status={{
                            touched: props.touched.maritalStatusValue,
                            error: props.errors.maritalStatusValue
                          }}
                          isDisabled={PersonalStatusesLocked.includes(status)}
                        />
                        {props.touched.maritalStatusValue &&
                          props.errors.maritalStatusValue && (
                            <Text style={globalStyles.dangerAlert}>
                              {props.errors.maritalStatusValue}
                            </Text>
                          )}
                      </View>
                    </View>
                  </View>
                </Card>
                <View style={[globalStyles.mt2, globalStyles.mb5]}>
                  <CustomButton text={t('next')} onPress={props.handleSubmit} />
                </View>
              </View>
            )}
          </Formik >
        </View >
      </ >
    )
  )
}

const styles = StyleSheet.create({
  required: {
    color: "red"
  }
})

export default BasicInformationForm

